import React, { useState, useEffect, useRef } from 'react';
import { ClipboardList, CheckCircle2, XCircle } from 'lucide-react';
import { StepNavButtons } from './StepNavButtons';
import { MarkdownContent } from '../MarkdownContent';
import { explanationFor, type ModuleStep, type StepResultResponse, type ExplanationContent } from '@/lib/models/module';

interface QuizQuestion extends ExplanationContent {
    question_id: number;
    question_type: string;
    question_text: string;
    options?: { option_id: string; text: string }[];
    correct_option_id?: string;
}

interface QuizStepRendererProps {
    step: ModuleStep;
    /** `{ answers: { "0": "…" } }` previously submitted for this step. */
    savedAnswer?: any;
    onSubmit: (answer: any) => Promise<StepResultResponse>;
    isSubmitting: boolean;
    canGoBack: boolean;
    onGoBack: () => void;
}

// #293 — an OPEN question inside a quiz. It is collected, never graded, and so
// can never be among wrong_questions: the candidate must not be sent back to
// "correct" a paragraph the server did not judge.
const isOpenQuestion = (q: QuizQuestion): boolean =>
    q.question_type === 'text_input' || q.question_type === 'ai_evaluated';

export const QuizStepRenderer: React.FC<QuizStepRendererProps> = ({ step, savedAnswer, onSubmit, isSubmitting, canGoBack, onGoBack }) => {
    const questions: QuizQuestion[] = step.content.questions || [];
    // Track answers by array index (matching backend expectation).
    //
    // Seeded from the previously submitted answer, but for the OPEN questions
    // only: a multiple-choice question is meant to be re-taken on a revisit,
    // while retyping a paragraph you already wrote is pure loss (#293).
    const [answers, setAnswers] = useState<Record<number, string>>(() => {
        const saved = savedAnswer?.answers;
        if (!saved || typeof saved !== 'object') return {};
        const seed: Record<number, string> = {};
        questions.forEach((q, idx) => {
            if (!isOpenQuestion(q)) return;
            const prev = saved[String(idx)];
            if (typeof prev === 'string' && prev.trim().length > 0) seed[idx] = prev;
        });
        return seed;
    });
    const [result, setResult] = useState<StepResultResponse | null>(null);
    const wrongSet = new Set(result?.wrong_questions || []);
    const bannerRef = useRef<HTMLDivElement>(null);

    // After grading, bring the result banner into view — the candidate is usually
    // scrolled to the last question and would otherwise never see it.
    useEffect(() => {
        if (result && bannerRef.current) {
            bannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [result]);

    const setAnswer = (idx: number, value: string) => {
        setAnswers((prev) => ({ ...prev, [idx]: value }));
    };

    // An open question counts as answered once it has non-whitespace text.
    const allAnswered = questions.every((q, idx) =>
        isOpenQuestion(q) ? (answers[idx] || '').trim().length > 0 : !!answers[idx],
    );

    const handleSubmit = async () => {
        const answerMap: Record<string, string> = {};
        for (let i = 0; i < questions.length; i++) {
            answerMap[String(i)] = answers[i] || '';
        }
        const res = await onSubmit({ answers: answerMap });
        setResult(res);
    };

    const handleRetry = () => {
        setResult(null);
        const cleaned = { ...answers };
        for (const wIdx of wrongSet) {
            // Never wipe an open answer on retry. wrong_questions should not
            // contain one, but if a future server ever reported it, discarding
            // a candidate's typed paragraph is the most destructive way to be
            // wrong about it.
            if (questions[wIdx] && isOpenQuestion(questions[wIdx])) continue;
            delete cleaned[wIdx];
        }
        setAnswers(cleaned);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ClipboardList className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-900">{step.step_title}</h2>
                    <p className="text-sm text-gray-500 mt-1">Svara på alla frågor nedan</p>
                </div>
            </div>

            {/* Result banner (scrolled into view after grading) */}
            {result && (
                <div ref={bannerRef} className={`p-4 rounded-xl border-2 ${result.correct ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-center gap-2">
                        {result.correct
                            ? <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                            : <XCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        }
                        <span className="font-semibold text-gray-900">
                            {result.correct
                                ? 'Alla svar korrekta! 🎉'
                                : 'Inte alla svar var korrekta. Korrigera de markerade frågorna och försök igen.'}
                        </span>
                    </div>
                </div>
            )}

            {/* Questions */}
            <div className="space-y-6">
                {questions.map((q, idx) => {
                    const open = isOpenQuestion(q);
                    const answered = !!result;
                    const questionWrong = wrongSet.has(idx) && !open;
                    const questionCorrect = answered && !questionWrong && !open;
                    // Card tint reflects per-question outcome once graded — but an
                    // open question has no outcome, so it stays neutral rather than
                    // going green and implying it was marked correct.
                    const cardCls = open
                        ? 'border-gray-200 bg-white'
                        : !answered
                        ? 'border-gray-200 bg-white'
                        : questionWrong
                            ? 'border-red-300 bg-red-50'
                            : 'border-green-300 bg-green-50/60';
                    return (
                        <div
                            key={idx}
                            className={`p-4 sm:p-5 rounded-2xl border transition-colors ${cardCls}`}
                        >
                            {/* Question — numbered badge + markdown body */}
                            <div className="mb-4 flex items-start gap-2.5">
                                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#1A4D4F]/10 text-xs font-bold text-[#1A4D4F]">
                                    {idx + 1}
                                </span>
                                <div className="flex-1">
                                    <MarkdownContent className="[&_p]:m-0 [&_p]:font-semibold [&_p]:text-gray-900">
                                        {q.question_text}
                                    </MarkdownContent>
                                </div>
                            </div>
                            {/* Open answer — free text, collected not graded (#293) */}
                            {open && (
                                <div className="space-y-2">
                                    <textarea
                                        disabled={answered}
                                        value={answers[idx] || ''}
                                        onChange={(e) => setAnswer(idx, e.target.value)}
                                        placeholder="Skriv ditt svar här..."
                                        rows={5}
                                        className="w-full p-3 rounded-xl border-2 border-gray-200 focus:border-[#1A4D4F] focus:ring-1 focus:ring-[#1A4D4F] outline-none resize-y text-sm disabled:bg-gray-50 disabled:text-gray-600"
                                    />
                                    {!answered && (
                                        <p className="text-xs text-gray-500">
                                            Fritextsvar — sparas och läses av arbetsgivaren.
                                        </p>
                                    )}
                                </div>
                            )}

                            {!open && (
                            <div className="space-y-2">
                                {q.options?.map((opt) => {
                                    const isSelected = answers[idx] === opt.option_id;
                                    // After grading: mark the picked option ✓/✗, and reveal the
                                    // correct one on a question the candidate got wrong.
                                    //
                                    // #313 — "got wrong" is now actually enforced. Without the
                                    // questionWrong guard, a question reported correct greened
                                    // BOTH the candidate's pick and the real answer, which is how
                                    // the ungraded-quiz bug surfaced visually: two green rows and
                                    // a cheerful "Alla svar korrekta".
                                    const revealCorrect = answered && questionWrong && q.correct_option_id != null && opt.option_id === q.correct_option_id;
                                    const showCorrect = answered && ((isSelected && questionCorrect) || revealCorrect);
                                    const showWrong = answered && isSelected && questionWrong;

                                    let optCls: string;
                                    if (showCorrect) optCls = 'border-green-500 bg-green-50';
                                    else if (showWrong) optCls = 'border-red-400 bg-red-50';
                                    else if (isSelected && !answered) optCls = 'border-[#1A4D4F] bg-[#A4C3B2]/20 shadow-sm';
                                    else if (answered) optCls = 'border-gray-200 opacity-60';
                                    else optCls = 'border-gray-200 hover:border-[#1A4D4F]/40 hover:bg-gray-50';

                                    return (
                                        <button
                                            key={opt.option_id}
                                            disabled={answered}
                                            onClick={() => setAnswer(idx, opt.option_id)}
                                            className={`group flex w-full items-center gap-3 rounded-xl border-2 p-3.5 text-left transition-all disabled:cursor-default ${optCls}`}
                                        >
                                            {/* Indicator: ✓/✗ after grading, otherwise a radio */}
                                            {showCorrect ? (
                                                <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600" />
                                            ) : showWrong ? (
                                                <XCircle className="h-5 w-5 flex-shrink-0 text-red-500" />
                                            ) : (
                                                <span
                                                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                                                        isSelected
                                                            ? 'border-[#1A4D4F] bg-[#1A4D4F]'
                                                            : 'border-gray-300 group-hover:border-[#1A4D4F]/50'
                                                    }`}
                                                >
                                                    {isSelected && <span className="h-2 w-2 rounded-full bg-white" />}
                                                </span>
                                            )}
                                            <span className="text-sm font-medium text-gray-900">{opt.text}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            )}

                            {/* #294 — the explanation the author wrote for THIS question.
                                It was collected by the editor and then never rendered: the
                                quiz was the one graded step that told the candidate nothing
                                about WHY an answer was right or wrong. Per question, because
                                a quiz grades several at once and one banner cannot explain
                                them all. */}
                            {answered && (() => {
                                // An open question has no outcome, so it gets the general
                                // explanation only — resolving by outcome would hand it
                                // explanation_incorrect and scold an ungraded answer.
                                const text = open ? q.explanation : explanationFor(q, questionCorrect);
                                if (!text) return null;
                                return (
                                    <div className={`mt-3 rounded-xl border p-3 ${open ? 'border-gray-200 bg-gray-50' : questionWrong ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
                                        <MarkdownContent className="[&_p]:m-0 [&_p]:text-sm [&_p]:text-gray-700">
                                            {text}
                                        </MarkdownContent>
                                    </div>
                                );
                            })()}
                        </div>
                    );
                })}
            </div>

            {/* Submit / Retry / Continue */}
            {result?.correct ? (
                <StepNavButtons
                    canGoBack={canGoBack}
                    onGoBack={onGoBack}
                    onNext={() => onSubmit({ _advance: true })}
                    nextLabel="Fortsätt"
                />
            ) : result && !result.correct ? (
                <StepNavButtons
                    canGoBack={canGoBack}
                    onGoBack={onGoBack}
                    onNext={handleRetry}
                    nextLabel="Försök igen"
                />
            ) : (
                <StepNavButtons
                    canGoBack={canGoBack}
                    onGoBack={onGoBack}
                    onNext={handleSubmit}
                    nextLabel={isSubmitting ? 'Kontrollerar...' : 'Skicka in'}
                    nextDisabled={!allAnswered || isSubmitting}
                />
            )}
        </div>
    );
};
